import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import {
  isViewContextExpired,
  subscribeActiveViewContext,
  type ViewContext,
} from '../../services/ViewContextService'
import { emitSensoryEvent } from '../../services/aliveness/SensoryEventBus'
import { isGhostEchoDirty, subscribeGhostEchoDirty } from '../../utils/ghostEchoDirtyRegistry'

type ApplyResult = 'APPLIED' | 'UNAVAILABLE'

type GhostEchoListenerProps = {
  onApply: (context: ViewContext) => ApplyResult
}

const lastSyncStorageKey = (uid: string) => `ghostEcho.lastAppliedSyncId.${uid}`

const readLastAppliedSyncId = (uid: string): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(lastSyncStorageKey(uid))
  } catch {
    return null
  }
}

const writeLastAppliedSyncId = (uid: string, syncId: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(lastSyncStorageKey(uid), syncId)
  } catch {
    // ignore
  }
}

const isNewerSync = (uid: string, syncId: string): boolean => {
  const lastApplied = readLastAppliedSyncId(uid)
  return !lastApplied || syncId > lastApplied
}

export const GhostEchoListener = ({ onApply }: GhostEchoListenerProps) => {
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const pendingSyncRef = useRef<ViewContext | null>(null)
  const dirtyRef = useRef(isGhostEchoDirty())

  useEffect(() => subscribeGhostEchoDirty((dirty) => { dirtyRef.current = dirty }), [])

  const apply = useCallback((context: ViewContext, source: 'live' | 'resume') => {
    if (!currentUser?.uid || !isNewerSync(currentUser.uid, context.syncId)) return
    if (isViewContextExpired(context)) {
      writeLastAppliedSyncId(currentUser.uid, context.syncId)
      return
    }
    const result = onApply(context)
    writeLastAppliedSyncId(currentUser.uid, context.syncId)
    if (result === 'APPLIED') {
      emitSensoryEvent('GHOST_ECHO_NAVIGATION', {
        source: 'ghost-echo',
        targetId: context.taskId ?? context.entityId ?? null,
        x: context.scrollOffset?.x ?? 0,
        y: context.scrollOffset?.y ?? 0,
      })
      showToast(
        source === 'resume'
          ? `Opening view from ${context.workspaceName}...`
          : `Syncing view from ${context.workspaceName}...`,
        'ghost',
      )
      return
    }
    showToast('View unavailable.', 'ghost')
  }, [currentUser?.uid, onApply, showToast])

  useEffect(() => {
    if (!currentUser?.uid) return
    return subscribeActiveViewContext(currentUser.uid, (context) => {
      if (!context || !isNewerSync(currentUser.uid, context.syncId)) return
      if (isViewContextExpired(context)) {
        writeLastAppliedSyncId(currentUser.uid, context.syncId)
        return
      }
      if (dirtyRef.current) {
        pendingSyncRef.current = context
        showToast('Desktop view ready.', 'ghost', {
          label: 'Resume Desktop View',
          run: () => {
            const next = pendingSyncRef.current ?? context
            pendingSyncRef.current = null
            apply(next, 'resume')
          },
        })
        return
      }
      apply(context, 'live')
    }, (error) => {
      console.warn('[ghost-echo] listener failed', error)
    })
  }, [apply, currentUser?.uid, showToast])

  return null
}
